// Header location switcher (U4) — legacy AppComponent `onLocationChange`:
// `POST location {location_id}` through the session location store.

import { useEffect, useMemo, useState } from 'react';
import { Alert, CircularProgress, MenuItem, Snackbar, TextField, Tooltip } from '@mui/material';
import { Location } from 'iconsax-reactjs';

import { isSuperUser } from '../../../auth/permissions';
import { apiErrorMessage } from '../../../api/client';
import { ensureLocation, selectLocation, useSessionLocation } from '../../../session/location';
import { t } from '../../../i18n';
import type { ShellUser } from '../api';

export function LocationSwitcher({ user }: { user: ShellUser | null }) {
  const { locations, currentId, loaded } = useSessionLocation();
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  // Initialise the backend session location right after login (legacy forced a
  // pick before any location-gated screen).
  useEffect(() => {
    if (!user) return;
    ensureLocation(user.location_id ?? null).catch((error) => setToast({ msg: apiErrorMessage(error, t('shell.location.none')), sev: 'error' }));
  }, [user]);

  // Non-super users only see the locations assigned to them (when any are).
  const options = useMemo(() => {
    const assigned = user?.locations?.map((l) => String(l.id)) ?? [];
    if (isSuperUser(user) || !assigned.length) return locations;
    return locations.filter((l) => assigned.includes(l.value) || l.value === currentId);
  }, [currentId, locations, user]);

  if (!loaded && !locations.length) return <CircularProgress size={16} />;
  if (!options.length) return null;

  const onChange = async (id: string) => {
    if (!id || id === currentId) return;
    setBusy(true);
    try {
      await selectLocation(id);
      setToast({ msg: t('shell.location.changed'), sev: 'success' });
    } catch (error) {
      setToast({ msg: apiErrorMessage(error, t('common.saveFailed')), sev: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={t('shell.location.label')} placement="bottom">
        <TextField
          select
          size="small"
          value={currentId ?? ''}
          disabled={busy || options.length < 2}
          onChange={(event) => void onChange(event.target.value)}
          inputProps={{ 'aria-label': t('shell.location.label'), 'data-testid': 'location-switcher' }}
          sx={{ minWidth: 140, maxWidth: 220, '& .MuiSelect-select': { py: 0.75, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.75 } }}
          SelectProps={{
            renderValue: (value) => (
              <>
                <Location size={15} />
                {options.find((l) => l.value === value)?.label ?? t('shell.location.select')}
              </>
            ),
            displayEmpty: true
          }}
        >
          {options.map((location) => (
            <MenuItem key={location.value} value={location.value}>
              {location.label}
            </MenuItem>
          ))}
        </TextField>
      </Tooltip>
      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
